import {
  DEFAULT_APP_BASE_DOMAIN,
  getClientHostOrgMap,
  RESERVED_CLIENT_HOSTS,
} from '@/config/client-hosts';
import { isSoulMapMode } from '@/utils/app-mode';
import {
  getSoulMapSocialOrganization,
  isSoulMapLoginFlow,
  isSoulMapLoginPage,
} from '@/utils/soulmap-auth';

export interface ResolveAuth0OrganizationOptions {
  /** Explicit ?org= query parameter — highest priority. */
  queryOrg?: string | null;
  hostname?: string;
  /** SoulMap login — ignore host mapping, use env org only. */
  envOrganizationOnly?: boolean;
}

function normalizeOrgId(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

function getAppBaseDomain(): string {
  return (
    import.meta.env.VITE_APP_BASE_DOMAIN?.trim().toLowerCase() ||
    DEFAULT_APP_BASE_DOMAIN
  );
}

/**
 * Extract the client host key from the current hostname.
 * e.g. heb.mentoralift.ai → "heb"
 */
export function getClientHostKey(hostname?: string): string | null {
  if (typeof window === 'undefined' && !hostname) {
    return null;
  }

  const host = (hostname ?? window.location.hostname).trim().toLowerCase();
  if (!host) return null;

  const devOverride = import.meta.env.VITE_DEV_CLIENT_HOST?.trim().toLowerCase();
  if (
    devOverride &&
    (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost'))
  ) {
    return devOverride;
  }

  const baseDomain = getAppBaseDomain();
  if (host === baseDomain || host === `www.${baseDomain}`) {
    return null;
  }

  const suffix = `.${baseDomain}`;
  if (!host.endsWith(suffix)) {
    return null;
  }

  const subdomain = host.slice(0, -suffix.length);
  if (!subdomain || subdomain.includes('.')) {
    return null;
  }

  if (RESERVED_CLIENT_HOSTS.has(subdomain)) {
    return null;
  }

  return subdomain;
}

export function getAuth0OrganizationForHost(hostname?: string): string | null {
  const hostKey = getClientHostKey(hostname);
  if (!hostKey) return null;

  return getClientHostOrgMap()[hostKey] ?? null;
}

/**
 * Enterprise SSO (LiFT login page): only ?org= or a dedicated client subdomain.
 * Does not use VITE_AUTH0_SOCIAL_ORGANIZATION — that would skip the org picker.
 */
export function resolveEnterpriseSsoOrganization(
  options: Pick<ResolveAuth0OrganizationOptions, 'queryOrg' | 'hostname'> = {}
): string | null {
  const queryOrg = normalizeOrgId(options.queryOrg);
  if (queryOrg) {
    return queryOrg;
  }

  return getAuth0OrganizationForHost(options.hostname);
}

/**
 * Social / Google login: ?org= → client subdomain → VITE_AUTH0_SOCIAL_ORGANIZATION.
 */
export function resolveSocialLoginOrganization(
  options: ResolveAuth0OrganizationOptions = {}
): string | null {
  if (options.envOrganizationOnly) {
    return normalizeOrgId(import.meta.env.VITE_AUTH0_SOCIAL_ORGANIZATION);
  }

  const queryOrg = normalizeOrgId(options.queryOrg);
  if (queryOrg) {
    return queryOrg;
  }

  const hostOrg = getAuth0OrganizationForHost(options.hostname);
  if (hostOrg) {
    return hostOrg;
  }

  return normalizeOrgId(import.meta.env.VITE_AUTH0_SOCIAL_ORGANIZATION);
}

/** Org to send on silent token calls so the cache key matches loginWithPopup. */
export function resolveSilentAuth0Organization(): string | null {
  if (isSoulMapLoginFlow() || isSoulMapLoginPage() || isSoulMapMode()) {
    return getSoulMapSocialOrganization();
  }

  return resolveEnterpriseSsoOrganization();
}
